import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Users, Clock, Award, Star, Heart } from 'lucide-react';
import { GlassCard } from './GlassCard';

/**
 * WhyChooseUs Component - DARK EDITION
 * Built for high-contrast trust building on a black background.
 */
const WhyChooseUs = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const content = {
    title: isAr ? 'لماذا تختار رايت تشويس؟' : 'Why Choose Right Choice?',
    subtitle: isAr
      ? 'نحن نضع مصلحة عائلتك في المقام الأول من خلال رعاية متخصصة وموثوقة.'
      : 'We put your family first with specialized, trustworthy, and compassionate home care.',
    features: [
      {
        icon: <Users className='text-medical-blue-400' size={32} />,
        title: isAr ? 'طاقم مدرب ومحترف' : 'Trained Professionals',
        description: isAr
          ? 'ممرضات ومقدمو رعاية ذوي خبرة عالية في الرعاية المنزلية.'
          : 'Highly experienced nurses and caregivers specialized in home environments.',
      },
      {
        icon: <ShieldCheck className='text-trust-teal' size={32} />,
        title: isAr ? 'رعاية آمنة وموثوقة' : 'Safe & Reliable',
        description: isAr
          ? 'نلتزم بأعلى معايير السلامة والأمان لكل مريض.'
          : 'We adhere to the highest safety and security standards for every patient.',
      },
      {
        icon: <Clock className='text-medical-blue-400' size={32} />,
        title: isAr ? 'استجابة سريعة' : 'Fast Response',
        description: isAr
          ? 'فريقنا متاح دائماً للرد على استفساراتكم وتلبية احتياجاتكم.'
          : 'Our team is always available to respond to your needs and inquiries.',
      },
      {
        icon: <Award className='text-trust-teal' size={32} />,
        title: isAr ? 'باقات مرنة' : 'Flexible Packages',
        description: isAr
          ? 'خيارات متنوعة للرعاية اليومية، الأسبوعية، أو الشهرية.'
          : 'Various options for daily, weekly, or monthly home care services.',
      },
    ],
  };

  return (
    <section
      className='py-24 bg-[#050505] relative overflow-hidden' // Changed to Deep Black
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Background Subtle Pattern - Inverted for dark mode */}
      <div className='absolute inset-0 opacity-[0.05] pointer-events-none'>
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] invert" />
      </div>

      <div className='section-padding max-w-7xl mx-auto relative z-10'>
        <div className='text-center max-w-3xl mx-auto mb-20'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-medical-blue-400 rounded-full text-xs font-black uppercase tracking-widest mb-4'
          >
            <Star size={14} fill='currentColor' />
            {isAr ? 'تميزنا' : 'Our Excellence'}
          </motion.div>

          <h2 className='text-4xl md:text-5xl font-black text-white mb-6'>
            {' '}
            {/* Changed to text-white */}
            {content.title}
          </h2>
          <p className='text-lg text-gray-400 font-medium'>
            {' '}
            {/* Changed to text-gray-400 */}
            {content.subtitle}
          </p>
        </div>

        <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
          {content.features.map((feature, index) => (
            <GlassCard
              key={index}
              delay={index * 0.1}
              className='p-8 hover:bg-white/5 transition-colors border-white/5' // Dark glass styling
            >
              <div className='mb-6 inline-block p-4 bg-white/5 rounded-2xl border border-white/10'>
                {feature.icon}
              </div>
              <h3 className='text-xl font-black text-white mb-4'>
                {feature.title}
              </h3>
              <p className='text-sm text-gray-400 font-medium leading-relaxed'>
                {feature.description}
              </p>
            </GlassCard>
          ))}
        </div>

        {/* Professional Trust Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='mt-20 py-10 border-t border-white/5 flex flex-wrap justify-center gap-12 lg:gap-24'
        >
          <div className='flex items-center gap-3'>
            <span className='text-4xl font-black text-medical-blue-400'>
              100%
            </span>
            <span className='text-xs font-bold text-gray-500 uppercase tracking-widest text-left'>
              {isAr ? 'رضا' : 'Patient'}
              <br />
              {isAr ? 'المرضى' : 'Satisfaction'}
            </span>
          </div>
          <div className='flex items-center gap-3'>
            <Heart className='text-red-500' size={40} />
            <span className='text-xs font-bold text-gray-500 uppercase tracking-widest text-left'>
              {isAr ? 'رعاية' : 'Compassionate'}
              <br />
              {isAr ? 'رحيمة' : 'Approach'}
            </span>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-4xl font-black text-trust-teal'>24/7</span>
            <span className='text-xs font-bold text-gray-500 uppercase tracking-widest text-left'>
              {isAr ? 'دعم' : 'Expert'}
              <br />
              {isAr ? 'متواصل' : 'Support'}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
